import { z } from 'zod';
import { DateRangeSchema, UuidSchema } from './common';
import {
  ChannelKindSchema,
  IncidentStatusSchema,
  RequestStatusSchema,
  RequestTypeSchema,
  RunStatusSchema,
} from './enums';
import { IntegrationStateSchema } from './integration';
import {
  IncidentCountsSchema,
  ProjectMetricsSchema,
  RatingsSchema,
  RequestStatusCountsSchema,
  RequestTypeCountsSchema,
  SurveyCountsSchema,
} from './metrics';

/**
 * §14 ТЗ. Дашборд: показатели только по данным внешней PostgreSQL.
 *
 * Один запрос отдаёт весь экран. Дробить его на плитку-на-запрос незачем:
 * показатели считаются по одному и тому же срезу, и десять параллельных
 * выборок дали бы десять разных «сейчас».
 */

/** §14: период до 12 месяцев. Всё, что шире, — отказ, а не молчаливое усечение. */
export const DASHBOARD_MAX_RANGE_DAYS = 366;
/** Умолчание, когда период не задан: три месяца дают видимую динамику по неделям. */
export const DASHBOARD_DEFAULT_RANGE_DAYS = 90;
/** Сколько строк показывать в разрезах «по администраторам» и «открытые жалобы». */
export const DASHBOARD_BREAKDOWN_LIMIT = 20;

/**
 * Фильтры §14. Применяются ко **всему** срезу разом, а не к отдельным плиткам:
 * иначе одна и та же цифра значила бы разное в соседних блоках.
 */
export const DashboardQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  projectId: UuidSchema.optional(),
  managerId: UuidSchema.optional(),
  /** администратор заказчика (§5): персона, а не связь */
  personId: UuidSchema.optional(),
  channel: ChannelKindSchema.optional(),
  runStatus: RunStatusSchema.optional(),
  /** наличие негатива. Строка из query, не coerce: Boolean('false') === true */
  hasIncident: z
    .enum(['true', 'false'])
    .transform((v) => v === 'true')
    .optional(),
  incidentStatus: IncidentStatusSchema.optional(),
  requestType: RequestTypeSchema.optional(),
  requestStatus: RequestStatusSchema.optional(),
});
export type DashboardQuery = z.infer<typeof DashboardQuerySchema>;

// Счётчики опросов, оценки, инциденты и разрез по объектам — в `metrics.ts`:
// их же считает сводка раздела «Результаты опросов» (§13).

// ───────────────────────────── Обращения ─────────────────────────────

/** Строка списка открытых жалоб (§14). Текст обращения на дашборд не выносится. */
export const OpenComplaintSchema = z.object({
  id: UuidSchema,
  number: z.string(),
  projectId: UuidSchema,
  projectName: z.string(),
  /** §19: дату показываем в поясе объекта, значит он нужен вместе со строкой */
  projectTimezone: z.string(),
  personName: z.string(),
  status: RequestStatusSchema,
  createdAt: z.string().datetime(),
});
export type OpenComplaint = z.infer<typeof OpenComplaintSchema>;

export const RequestMetricsSchema = z.object({
  total: z.number().int().min(0),
  open: z.number().int().min(0),
  /** все три типа, включая нулевые */
  byType: z.array(RequestTypeCountsSchema).length(3),
  /** все пять статусов, включая нулевые */
  byStatus: z.array(RequestStatusCountsSchema).length(5),
  openComplaints: z.array(OpenComplaintSchema),
  openComplaintsTotal: z.number().int().min(0),
});
export type RequestMetrics = z.infer<typeof RequestMetricsSchema>;

// ───────────────────────────── Разрезы ─────────────────────────────

export const ManagerMetricsSchema = z.object({
  /** null — объект без назначенного менеджера (§6.1); строка «Не назначен» */
  managerId: UuidSchema.nullable(),
  managerName: z.string(),
  projects: z.number().int().min(0),
  runs: z.number().int().min(0),
  completed: z.number().int().min(0),
  responseRate: z.number().min(0).max(1).nullable(),
  average: z.number().nullable(),
  openIncidents: z.number().int().min(0),
});
export type ManagerMetrics = z.infer<typeof ManagerMetricsSchema>;

export const PersonMetricsSchema = z.object({
  personId: UuidSchema,
  personName: z.string(),
  projects: z.number().int().min(0),
  runs: z.number().int().min(0),
  completed: z.number().int().min(0),
  responseRate: z.number().min(0).max(1).nullable(),
  average: z.number().nullable(),
  incidents: z.number().int().min(0),
});
export type PersonMetrics = z.infer<typeof PersonMetricsSchema>;

/** Срез, который может не поместиться целиком: показываем начало и общий счёт. */
export const PersonBreakdownSchema = z.object({
  items: z.array(PersonMetricsSchema),
  total: z.number().int().min(0),
});
export type PersonBreakdown = z.infer<typeof PersonBreakdownSchema>;

// ───────────────────────────── Ответ целиком ─────────────────────────────

export const DashboardSummarySchema = z.object({
  /** фактически применённый период: сервер мог подставить умолчание */
  range: DateRangeSchema,
  generatedAt: z.string().datetime(),
  surveys: SurveyCountsSchema,
  ratings: RatingsSchema,
  incidents: IncidentCountsSchema,
  requests: RequestMetricsSchema,
  /** объекты среза; «худшие» — сортировка этого же массива, не отдельный запрос */
  byProject: z.array(ProjectMetricsSchema),
  byManager: z.array(ManagerMetricsSchema),
  byPerson: PersonBreakdownSchema,
  /**
   * §14 просит состояние синхронизации, §3 не даёт менеджеру объекта доступа
   * к интеграции. При расхождении выигрывает более узкое право: без
   * `integration.read` здесь null, и плитки на экране нет.
   */
  integration: IntegrationStateSchema.nullable(),
});
export type DashboardSummary = z.infer<typeof DashboardSummarySchema>;
